/**
 * BATCH 2B — CyclePhaseProvider.
 *
 * Single React Context that fetches the canonical cycle phase from
 * GET /api/v2/economic/cycle/current and exposes it to every screen via
 * the `usePhase()` hook. Replaces the 5+ duplicate cycle fetches that
 * previously lived inside Dashboard, PortfolioDetail, PortfolioRebalancing,
 * InstrumentEvaluator, AdvancedCycleAnalysis and MarketAnalysis.
 *
 * Refresh strategy:
 *   - Fetch on mount
 *   - Refresh every 15 minutes
 *   - Refresh when the window regains focus (so a user returning after a
 *     long pause never sees a stale phase pill)
 *
 * Loading behaviour:
 *   - While the first request is pending, `phase` silently defaults to
 *     'expansion' and `loading` is true. Screens may use `loading` to
 *     suppress secondary phase-derived UI if desired.
 *
 * Error behaviour:
 *   - On any fetch failure the previous phase is retained; on the first
 *     fetch failure we retain the safe 'expansion' default. `error` holds
 *     the last error message for diagnostics.
 *
 * Auth:
 *   - The endpoint requires the user's session cookie / token. This
 *     provider mounts inside <AuthProvider> in App.js, so withCredentials +
 *     X-Session-Token (handled centrally by api/config) cover both auth
 *     paths the app uses.
 */
import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { API, getSessionToken } from '../config/api';
import { normalizePhase } from '../utils/cyclePhaseConfig';

const REFRESH_INTERVAL_MS = 15 * 60 * 1000; // 15 minutes

const DEFAULT_PHASE_DATA = {
  phase: 'Expansion',
  phase_lower: 'expansion',
  confidence: 'medium',
  source: 'default',
  stale: true,
  as_of: null,
  recession_probability: null,
  lei: null,
  cei: null,
};

const CyclePhaseContext = createContext({
  phase: 'expansion',
  phaseLabel: 'Expansion',
  data: DEFAULT_PHASE_DATA,
  loading: true,
  error: null,
  refresh: async () => {},
});

export function CyclePhaseProvider({ children }) {
  const [data, setData] = useState(DEFAULT_PHASE_DATA);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);
  const hasMountedRef = useRef(false);

  const fetchPhase = useCallback(async () => {
    try {
      const token = getSessionToken();
      const headers = token ? { 'X-Session-Token': token } : {};
      // Note: API base already includes `/api/v2`, so `${API}/economic/cycle/current`
      // resolves to `${BACKEND_URL}/api/v2/economic/cycle/current`.
      const res = await fetch(`${API}/economic/cycle/current`, {
        method: 'GET',
        credentials: 'include',
        headers,
      });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const json = await res.json();
      // Backend already returns canonical Title-case phase + phase_lower.
      // Normalize defensively in case future backend changes leak
      // legacy taxonomy through this endpoint.
      const phaseLower = normalizePhase(json.phase_lower || json.phase);
      const phaseLabel =
        phaseLower.charAt(0).toUpperCase() + phaseLower.slice(1);
      setData({ ...json, phase: phaseLabel, phase_lower: phaseLower });
      setError(null);
    } catch (err) {
      // Keep previous data on error — never clobber a known-good phase
      // with the safe default once we have one.
      setError(err?.message || 'Failed to load cycle phase');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch + 15-minute interval
  useEffect(() => {
    if (hasMountedRef.current) return;
    hasMountedRef.current = true;
    fetchPhase();
    intervalRef.current = setInterval(fetchPhase, REFRESH_INTERVAL_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchPhase]);

  // Refresh on window focus
  useEffect(() => {
    const onFocus = () => {
      fetchPhase();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [fetchPhase]);

  const value = {
    phase: data.phase_lower || 'expansion',
    phaseLabel: data.phase || 'Expansion',
    data,
    loading,
    error,
    refresh: fetchPhase,
  };

  return (
    <CyclePhaseContext.Provider value={value}>
      {children}
    </CyclePhaseContext.Provider>
  );
}

/**
 * Hook to consume the canonical cycle phase. Always returns a value,
 * even before the first fetch resolves (silent default to Expansion).
 *
 * Usage:
 *   const { phase, phaseLabel, data, loading } = usePhase();
 *   const cfg = getPhaseConfig(phase);
 */
export function usePhase() {
  return useContext(CyclePhaseContext);
}

export default CyclePhaseContext;
